import _ from 'lodash';

import { AsyncForEach } from 'assets/js/mixins/methods';

const MAX_RETRIES = 3;

const errHandler = (err, onErrorFn) => {
  if (!_.isNull(onErrorFn) && _.isFunction(onErrorFn)) {
    onErrorFn(err);
  } else {
    throw err;
  }
};

// ResetColumnValuesReservables resets the column values to their defaults
// for a site or portal reservables.
export const ResetColumnValuesReservables = async ({
  store = null,
  key = null,
  subKey = null,
  defaultData = null,
  onErrorFn = null,
  onSuccessFn = null,
}) => {
  if (_.isNull(store)) throw Error('ResetColumnValuesReservables: store null');
  if (_.isNull(key)) throw Error('ResetColumnValuesReservables: key null');
  if (_.isNull(subKey)) {
    throw Error('ResetColumnValuesReservables: subKey null');
  }
  if (_.isNull(defaultData)) {
    throw Error('ResetColumnValuesReservables: defaultData null');
  }

  try {
    const valueCols = _.cloneDeep(defaultData);

    let oldSettings;
    try {
      oldSettings = store.getters.GetKeyStore(key);
    } catch (errMissing) {
      console.warn('Missing stored column values, attempting to reset ' +
        'settings now');
    }

    // Migration code for the old method of columns.
    if (_.get(oldSettings, 'columns')) {
      valueCols[0] = _.get(oldSettings, 'columns[0]', defaultData[0]);
      valueCols[1] = _.get(oldSettings, 'columns[1]', defaultData[1]);
    }

    await store.dispatch('MutateKeyStore',
      { key, path: `${subKey}.columns`, value: valueCols });
    await store.dispatch('MutateKeyStore',
      { key, path: `${subKey}.perPage`, value: 5 });
  } catch (err) {
    errHandler(err, onErrorFn);
  }
  if (!_.isNull(onSuccessFn) && _.isFunction(onSuccessFn)) onSuccessFn();
};

// ResetValues resets the admin user page values to their defaults.
export const ResetValues = async ({
  store = null,
  key = null,
  subKey = null,
  defaultData = null,
  onErrorFn = null,
  onSuccessFn = null,
}) => {
  try {
    if (_.isPlainObject(defaultData) && !_.isNull(subKey) &&
      !_.isUndefined(subKey)) {
      await AsyncForEach(defaultData, async (dataV, dataK) => {
        await store.dispatch('MutateKeyStore',
          { key, path: `${subKey}.${dataK}`, value: dataV, storeValue: false });
      });
      await store.dispatch('ForceStoreKey', { key });
    } else {
      await store.dispatch('MutateKeyStore',
        { key, path: subKey, value: defaultData });
    }
  } catch (err) {
    errHandler(err, onErrorFn);
  }
  if (!_.isNull(onSuccessFn) && _.isFunction(onSuccessFn)) onSuccessFn();
};

// fetchSettings fetches the JSON encoded settings for the site or portal
// for this user from the database, then decodes it and sets it correctly in
// the local context.
export const FetchSettings = async ({
  store = null,
  key = null,
  subKey = null,
  validatorFn = null,
  defaultData = null,
  resetFn = null,
  onErrorFn = null,
  onSuccessFn = null,
  invocation = 0,
}) => {
  if (_.isNull(store)) throw Error('FetchSettings: store null');
  if (_.isNull(key)) throw Error('FetchSettings: key null');
  if (_.isNull(validatorFn)) throw Error('FetchSettings: validatorFn null');
  if (_.isNull(defaultData)) {
    throw Error('FetchSettings: defaultData null');
  }
  if (_.isNull(resetFn)) throw Error('FetchSettings: resetFn null');

  try {
    await store.dispatch('PopulateKeyStore', key, defaultData);
  } catch (err) {
    if (err.name !== 'StatusCode404') {
      if (!_.isNull(onErrorFn) && _.isFunction(onErrorFn)) {
        onErrorFn(err);
        return false;
      } else {
        throw err;
      }
    }
  }

  let settings = null;
  try {
    settings = store.getters.GetKeyStore(key, subKey);
    validatorFn(settings);
  } catch (err) {
    if (err.name === 'KeyError' || err.name === 'ValidationError') {
      // Populate with the default values.
      await resetFn({ store, key, subKey, defaultData, onErrorFn });
      try {
        settings = store.getters.GetKeyStore(key, subKey);

        if (invocation < MAX_RETRIES) {
          settings = await FetchSettings({
            store,
            key,
            subKey,
            validatorFn,
            defaultData,
            resetFn,
            onErrorFn,
            onSuccessFn,
            invocation: invocation + 1,
          });
        } else {
          throw err;
        }
      } catch (errReset) {
        const errLocal = Error('failed to reset the settings key store: ' +
          `${errReset}`);
        errHandler(errLocal, onErrorFn);
      }
    }
  }

  if (!_.isNull(onSuccessFn) && _.isFunction(onSuccessFn)) onSuccessFn();
  return settings;
};
